import type { PaginationMeta } from '@eliadmin/core/src/api/api-types';
import type { PlaceOfDelivery } from '@eliadmin/core/src/api/reservation-medicine/reservation-medicine-api';

import { fetchData } from '@eliadmin/core/src/api/fetcher';

/** A pickup point of the administration module „Miesta doručenia“ (the personal pickup of the cart). */
export type Store = {
  id: number;
  name: string;
  /** Street with number and city, then postcode and country. */
  lines: string[];
  /** What the map looks up: the coordinates, or the address when they are missing. */
  map: string;
};

/** Pickup points per request while the list is browsed – the network has hundreds of them. */
export const STORES_PAGE_SIZE = 30;
/** Pickup points per request when all are needed (the search matches cities and streets too). */
const STORES_BULK_SIZE = 200;

const join = (separator: string, ...parts: Array<string | null | undefined>) =>
  parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(separator);

export function toStore(place: PlaceOfDelivery): Store {
  const street = join(' ', place.address, place.street_number);
  const lines = [join(', ', street, place.city), join(', ', place.postcode, place.country?.name)];
  return {
    id: place.id,
    name: place.name.trim(),
    lines: lines.filter(Boolean),
    map:
      place.latitude && place.longitude
        ? `${place.latitude},${place.longitude}`
        : join(', ', street, join(' ', place.postcode, place.city)),
  };
}

/**
 * One page of pickup points in admin order; `hasMore` is false after the last page. `revalidate`
 * caches the server request (the first page of the list), the browser requests are not cached.
 */
export async function fetchStoresPage(
  locale: string,
  page: number,
  results = STORES_PAGE_SIZE,
  revalidate?: number,
  signal?: AbortSignal,
) {
  const { data, meta } = await fetchData<{ data: PlaceOfDelivery[]; meta: PaginationMeta }>({
    method: 'GET',
    uri: '/store/place-of-deliveries',
    locale,
    query: { results, page },
    ...(signal ? { signal } : {}),
    ...(revalidate
      ? { cache: 'force-cache' as const, next: { revalidate, tags: ['store-place-of-deliveries'] } }
      : {}),
  });
  return { stores: data.map(toStore), hasMore: meta.current_page < meta.last_page };
}

/**
 * Every pickup point, page by page – for the search: the API searches the names only, the list also
 * matches the cities and streets („Košice“ finds „Železo no 55“).
 * A cleared search or unmounted finder cancels the active request and remaining pages.
 */
export async function fetchAllStores(locale: string, signal?: AbortSignal) {
  const stores: Store[] = [];
  for (let page = 1; ; page += 1) {
    signal?.throwIfAborted();
    const result = await fetchStoresPage(locale, page, STORES_BULK_SIZE, undefined, signal);
    signal?.throwIfAborted();
    stores.push(...result.stores);
    if (!result.hasMore) return stores;
  }
}
